import { selMessage } from "@selector/shared";
import { type ColDef } from "ag-grid-community";
import { isEqual, keyBy, pickBy, sortBy } from "lodash-es";
import pluralize from "pluralize";
import { type Location } from "react-router";
import { type ActionCreator } from "redux";

import { type IDataTableQueryParams, type IInputTableQuerySpecWithContext } from "@/api/WebApiClient.ts";
import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { getAnalyzerConversionEntityTypes, urlJnlpPage, urlRedirectToGroupPage } from "@/app-utils/classic-portal.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { getFilterExpression } from "@/app-utils/filter.ts";
import { type GridComparator } from "@/app-utils/grid.ts";
import {
  checkAvailabilityOfIdentifiers,
  cloneSelectionModel,
  computeSelectionRange,
  emptySelectionModel,
  type ISelectionModel,
  setAllSelected,
  setMultipleSelected,
  setRangeSelected,
  setSelected,
  type TGridSelModelIdentifier,
} from "@/app-utils/selection-model.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import {
  type AnalyzerIdMapping,
  type AppConfiguration,
  type Column,
  type Gene,
  type Group,
  type IdRangePair,
  type InputAnalyzerIdMappingInput,
  type InputColumnInput,
  type InputRestriction,
  type InputTableQuerySpec,
  type MutationGroupByTypeArgs,
  type MutationGroupInLegacyPortalArgs,
  type RowIndex,
  type SearchResult,
  type Table,
  type TableViewState,
  type Template,
  type VariantAnalysisGroup,
  type View,
} from "@/graphql.ts";
import {
  ANALYZERS,
  BLAST_HITS_TABLE_COLUMNS,
  CELL_EXPANSION,
  CELL_TYPE,
  DATATABLE_ID_TYPE,
  DATATABLE_PARENT_TYPE,
  ENTITY_TYPE,
  FEATURE_DISPLAY_NAME,
  NON_GENOMIC_GENOME_ID,
  SORT_ORDER_DESC,
  TableRestriction,
  TRACK_TYPE,
  UNSPECIFIED_ENTITY_ID,
  VIEW,
} from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import { type ITableSpecWithContext } from "../../ui-modules/content-browser/ContentBrowserTableView.js";
import { type ICustomColDef } from "../../ui-modules/core/grid/DataTable.js";
import {
  CHANGE_TABLE_RESTRICTION,
  COPY_GROUP_TO_CLASSIC_PORTAL_FAILURE,
  COPY_GROUP_TO_CLASSIC_PORTAL_REQUEST,
  COPY_GROUP_TO_CLASSIC_PORTAL_SUCCESS,
  CREATE_SEARCH_RESULT_SUCCESS,
  DELETE_COLUMN_TEMPLATE_FAILURE,
  DELETE_COLUMN_TEMPLATE_REQUEST,
  DELETE_COLUMN_TEMPLATE_SUCCESS,
  DESELECT_ALL_TABLE_ROWS,
  DESTROY_ACTIVE_TABLE,
  ENSURE_ROW_VISIBLE,
  FETCH_COLUMN_TEMPLATES_FAILURE,
  FETCH_COLUMN_TEMPLATES_REQUEST,
  FETCH_COLUMN_TEMPLATES_SUCCESS,
  FILTER_BY_TABLE_COLUMN,
  GROUP_RELATED_FAILURE,
  GROUP_RELATED_REQUEST,
  GROUP_RELATED_SUCCESS,
  LOAD_GRID_COLUMN_FAILURE,
  LOAD_ROW_POSITION_FAILURE,
  LOAD_ROWS_FAILURE,
  LOAD_ROWS_IGNORE,
  LOAD_ROWS_REQUEST,
  LOAD_ROWS_SUCCESS,
  LOAD_SEARCH_RESULT_SUCCESS,
  LOAD_TABLE_COLUMNS_FAILURE,
  LOAD_TABLE_COLUMNS_REQUEST,
  LOAD_TABLE_COLUMNS_SUCCESS,
  LOAD_TABLE_FAILURE,
  LOAD_TABLE_REQUEST,
  LOAD_TABLE_SUCCESS,
  LOAD_VIEW_SUCCESS,
  RESET_DATA_TABLE,
  RESET_FILTER,
  SAVE_COLUMN_TEMPLATE_FAILURE,
  SAVE_COLUMN_TEMPLATE_REQUEST,
  SAVE_COLUMN_TEMPLATE_SUCCESS,
  SELECT_ALL_TABLE_ROWS,
  SELECT_COLUMN_TEMPLATE_FAILURE,
  SELECT_COLUMN_TEMPLATE_REQUEST,
  SELECT_COLUMN_TEMPLATE_SUCCESS,
  SELECT_TABLE,
  SELECT_TABLE_RANGE,
  SELECT_TABLE_ROW,
  SELECT_TABLE_ROWS,
  SET_COLUMN_LIST,
  SET_EXPANSION_PATH,
  SET_GLOBAL_FILTER,
  SET_REFERENCE_GENOME,
  SYNC_COLUMN_LIST_FAILURE,
  TRANSLATE_ANALYZER_IDS_FAILURE,
  TRIGGER_TABLE_REFRESH,
  UPDATE_COLUMN_TEMPLATE_FAILURE,
  UPDATE_COLUMN_TEMPLATE_REQUEST,
  UPDATE_COLUMN_TEMPLATE_SUCCESS,
  UPDATE_TABLE_GUIDANCE_STATE,
  UPDATE_TABLE_ROW,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { auditRouteChange } from "./auth.js";
import { parseTableColumnAndReplaceProjectTermWithSystemSetting } from "./config.js";
import { getTableSpecFromSortableTrack, type ISortableTrack } from "./genome-browser.js";

/**
 * State definition
 */
export interface ITableDataState {
  tables: ITableDataList;
  activeTable: InputTableQuerySpec;
  doNotShowPerfGuidance: boolean;
  rowRestriction: TableRestriction;
}

export type ITableDataList = Record<string, ITableData>; // map by tableKey

export interface IIdentifiable {
  id: TGridSelModelIdentifier;
  locked?: boolean;
  isSelectable?: boolean;
}

export interface ITableData {
  isLoading: boolean;
  isLoadingTableMeta: boolean;
  isLoadingTemplates: boolean;
  initialColumns: ITableColumn[]; // initial set of columns
  columns: ITableColumn[];
  columnTemplates: Template[];
  pageRows: IIdentifiable[]; // TODO should not be stored at all, but we sometimes do use row data to make some checks or get display names!
  totalRowCount: number;
  filteredRowCount: number;
  restrictedRowCount: number;
  rowIds: number[];

  selectionModel: ISelectionModel;
  filterExpression: string; // summary of filter expressions of each column, but this one is applied directly while the individual column filters are filled upon request success
  tableQuerySpec?: InputTableQuerySpec;
  expansionPath: string[]; // describing which columns or column groups should be expanded
  scrollToRowId: number;
  scrollToRowIndex: number;
  rowToUpdate: IIdentifiable;
}

export interface ITableColumn
  extends Column,
    Pick<
      ColDef,
      "suppressPaste" | "suppressFillHandle" | "editable" | "valueSetter" | "valueGetter" | "filterValueGetter"
    > {
  // filterExpression?: string;  // already exists in IColumn
  expensive?: boolean;
  openLinkExternal?: boolean;
  autoHeight?: boolean;
  comparator?: GridComparator;
  sortingOrder?: string[]; // from ag-grid
}

export interface IFilterPayload {
  columnField: string;
  tableSpec: InputTableQuerySpec;
  filterExpression: string;
}

export const initialTableData: ITableData = {
  isLoading: false,
  isLoadingTableMeta: false,
  isLoadingTemplates: false,
  columns: [],
  initialColumns: [],
  columnTemplates: [],
  pageRows: [],
  totalRowCount: -1,
  filteredRowCount: 0,
  restrictedRowCount: 0,
  rowIds: [],
  selectionModel: emptySelectionModel,
  tableQuerySpec: null,
  filterExpression: "",
  expansionPath: [],
  scrollToRowId: null,
  scrollToRowIndex: null,
  rowToUpdate: null,
};

export const initialTableDataState: ITableDataState = {
  doNotShowPerfGuidance: false,
  tables: {},
  activeTable: null,
  rowRestriction: TableRestriction.REGION,
};

/**
 * Selectors / Utils
 */

export const hasSelection = (table: ITableData): boolean => {
  return table && table.selectionModel.selectedIds.length > 0;
};

export const getTableDataKey = (tableSpec: ITableSpecWithContext | null | undefined): string => {
  const parentType: string = tableSpec?.queryType ?? tableSpec?.idType?.toLowerCase() ?? DATATABLE_ID_TYPE.ENTITY; // e.g. will be "gene" or "group"

  return `${parentType ? parentType + "-" : ""}${tableSpec?.resultType ?? ""}${
    tableSpec?.resultSubTypeId ? "-" + tableSpec.resultSubTypeId : ""
  }${tableSpec?.context ? "-" + tableSpec.context : ""}`;
};

export const getTableDataSlice = (
  tableDataState: ITableDataState,
  tableSpec: ITableSpecWithContext,
): ITableData | undefined => tableDataState.tables[getTableDataKey(tableSpec)];

// it would be better to embed directly the tableSpec into the track data
export const getTableDataSliceFromTrack = (
  tableDataState: ITableDataState,
  track: ISortableTrack,
  info?: { genomeId?: number; refGenomeId?: number; fileId?: number; coordinateListId?: number },
): ITableData | undefined => {
  // it is important to NOT make use of the intermediate table spec, this is only used to get our hands to the data slice, which hosts the GENUINE table query spec
  return getTableDataSlice(tableDataState, getTableSpecFromSortableTrack(track, info));
};

export const createTableSpecForOrganismParent = (
  entityType: string,
  parentId: number = null,
  refGenomeId: number = undefined,
  subTypeId: number = undefined,
): InputTableQuerySpec => {
  const refGenomeIdSpec = refGenomeId !== UNSPECIFIED_ENTITY_ID ? refGenomeId : undefined;
  return {
    id: parentId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: DATATABLE_PARENT_TYPE.ORGANISM,
    resultType: entityType,
    resultSubTypeId: subTypeId,
    restrictions: refGenomeIdSpec
      ? {
          refGenomeId: refGenomeIdSpec,
        }
      : undefined,
  };
};

export const createTableSpecForFileParent = (
  fileId: number,
  entityType: string,
  genomeId: number,
): InputTableQuerySpec => {
  return {
    id: fileId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.PROJECT_FILE,
    resultType: entityType,
    resultSubTypeId: fileId, // because the table key does not take the id into account currently
    restrictions: { refGenomeId: genomeId },
  };
};

export const createTableSpecForCoordinateListParent = (
  coordinateListId: number | null | undefined,
  entityType: string | null | undefined,
  genomeId: number | null | undefined,
): InputTableQuerySpec => {
  return {
    id: coordinateListId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: DATATABLE_PARENT_TYPE.COORD_LIST,
    resultType: entityType,
    resultSubTypeId: coordinateListId, // because the table key does not take the id into account currently
    restrictions: { refGenomeId: genomeId },
  };
};

export const createTableSpecForProteinParent = (
  entityType: string,
  parentId: number = null,
  subTypeId: number = undefined,
): InputTableQuerySpec => {
  return {
    id: parentId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: DATATABLE_PARENT_TYPE.PROTEIN,
    resultType: entityType,
    resultSubTypeId: subTypeId,
    restrictions: undefined,
  };
};

export const createTableSpecForContigParent = (
  entityType: string,
  parentId: number = null,
  subTypeId: number = undefined,
): InputTableQuerySpec => {
  return {
    id: parentId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: DATATABLE_PARENT_TYPE.CONTIG,
    resultType: entityType,
    resultSubTypeId: subTypeId,
    restrictions: undefined,
  };
};

export const createTableSpecForAnalysisGroupParent = (
  variantAnalysisGroup: VariantAnalysisGroup,
): InputTableQuerySpec => {
  return {
    id: variantAnalysisGroup?.id,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.ANALYSIS_GROUP,
    resultType: ENTITY_TYPE.ORGANISM,
  };
};

export const createTableSpecForParentTypeParent = (
  entityType: string,
  parentId: number = null,
  projectId = -1,
): InputTableQuerySpec => {
  return {
    id: parentId,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.PARENT_TYPE,
    resultType: entityType,
    restrictions: { projectId: projectId },
  };
};

export const createTableSpecForRootEntityType = (entityType: string, context?: string): ITableSpecWithContext => {
  return {
    id: -1,
    idType: DATATABLE_ID_TYPE.ENTITY,
    resultType: entityType,
    context: context,
  };
};

// allows to uniquely identify data related to a specific table specification e.g. as key for a component that should mount/unmount each time the table spec changes
export const createUniqueKeyFromTableSpec = (tableSpec: ITableSpecWithContext) =>
  // the restrictions are NOT included because we do not want a reset of the table when changed (including ref genome [SEL6-2271])
  tableSpec &&
  `${tableSpec.queryType || ""}-${tableSpec.resultType}-${tableSpec.id}-${tableSpec.resultSubTypeId || ""}-${
    tableSpec.context || ""
  }`;

export const createTableSpecForGroupParent = (
  entityType: string,
  parentId: number | null = null,
  subTypeId?: number,
): InputTableQuerySpec => {
  return {
    id: parentId,
    idType: DATATABLE_ID_TYPE.GROUP,
    resultType: entityType,
    resultSubTypeId: subTypeId,
  };
};

export const getInputTableQuerySpec = (tableData: ITableData): InputTableQuerySpec => {
  if (!tableData) {
    return {};
  }
  return {
    ...tableData.tableQuerySpec,
    rowFilter: getFilterExpression(tableData.columns),
    rowSorting: getSortOrderExpressionFromTableColumns(tableData.columns),
  };
};

export const getTableQuerySpecWithSelection = (
  tableData: ITableData,
  customSelection?: (string | number)[],
): InputTableQuerySpec => {
  if (!tableData) {
    return {};
  }
  const tableQuerySpec: InputTableQuerySpec = getInputTableQuerySpec(tableData);
  const selection = customSelection || (tableData.selectionModel && tableData.selectionModel.selectedIds);
  if (selection && selection.length > 0) {
    return {
      ...tableQuerySpec,
      restrictions: {
        ...tableQuerySpec.restrictions,
        selection: {
          selectedResultIds: selection,
        },
      },
    };
  }
  return tableQuerySpec;
};

export const getTableQuerySpecFromSummaryEntity = (entity: IIdentifiable, entityType: string): InputTableQuerySpec => {
  return {
    resultType: entityType,
    restrictions: {
      selection: {
        selectedResultIds: [entity.id],
      },
    },
  };
};

function restoreFromView(state: ITableDataState, tableView: TableViewState): ITableDataState {
  const { expansionPath, columns, totalRowCount, filteredRowCount, rowIds, tableQuerySpec, selectedIds } = tableView;

  return {
    ...updateTableDataWithProps(state, tableQuerySpec, {
      tableQuerySpec,
      isLoading: false,
      isLoadingTableMeta: false,
      initialColumns: columns,
      columns,
      totalRowCount,
      restrictedRowCount: totalRowCount,
      filteredRowCount,
      filterExpression: getFilterExpression(columns) || "",
      rowIds,
      selectionModel: {
        selectedIds,
        lastIndexOperation: 0,
        lastSingleSelectedId: -1,
        lastOperationIsSelection: true,
      },
      expansionPath: expansionPath || [],
    }),
    activeTable: tableQuerySpec,
  };
}

export function createSelectionSpecFromTableViewState(tableViewState: Partial<TableViewState>): InputTableQuerySpec {
  if (!tableViewState) {
    return null;
  }
  const columns = tableViewState.columns?.filter((column: Column) => !column.hidden);

  return {
    ...tableViewState.tableQuerySpec,
    rowFilter: getFilterExpression(columns),
    rowSorting: getSortOrderExpressionFromTableColumns(columns),
    restrictions: tableViewState.selectedIds?.length
      ? {
          selection: { selectedResultIds: tableViewState.selectedIds },
        }
      : undefined,
  };
}

/**
 * Reducers
 */
export default createReducer(initialTableDataState, {
  // The tableDataState is freshly initialized if a new search result was created
  // or if an existing search result was loaded via URL
  [CREATE_SEARCH_RESULT_SUCCESS]: (state: ITableDataState, payload: SearchResult) => {
    return getInitialTableDataStateForSearchResult(payload, state);
  },

  [LOAD_SEARCH_RESULT_SUCCESS]: (state: ITableDataState, payload: SearchResult) => {
    return getInitialTableDataStateForSearchResult(payload, state);
  },

  [LOAD_TABLE_REQUEST]: (state: ITableDataState, payload): ITableDataState => {
    const { tableSpec, filterExpression, selectionModel } = payload;
    return {
      ...updateTableDataWithProps(state, tableSpec, {
        isLoadingTableMeta: true,
        filterExpression: filterExpression,
        selectionModel: selectionModel,
      }),
      activeTable: tableSpec,
    };
  },

  [LOAD_TABLE_COLUMNS_REQUEST]: (state: ITableDataState, payload: any): ITableDataState => {
    const { tableSpec, filterExpression } = payload;

    return updateTableDataWithProps(state, tableSpec, {
      isLoadingTableMeta: true,
      filterExpression: filterExpression,
    });
  },

  [LOAD_ROWS_REQUEST]: (state: ITableDataState, tableSpec: InputTableQuerySpec) => {
    return updateTableDataWithProps(state, tableSpec, {
      isLoading: true,
    });
  },

  [LOAD_VIEW_SUCCESS]: (state: ITableDataState, view: View) => {
    if (view.viewType === VIEW.TABLE_VIEW.type) {
      return restoreFromView(state, view.state.tableView);
    } else if (view.viewType === VIEW.CONTENT_BROWSER_VIEW.type) {
      return restoreFromView(state, view.state.contentBrowserView.tableView);
    } else if (view.viewType === VIEW.DATA_VIEW.type && view.state.datasetView.groupTableView) {
      return restoreFromView(state, view.state.datasetView.groupTableView);
    } else if (view.viewType === VIEW.GENOME_VIEW.type) {
      let newState: ITableDataState = state;
      const featureTableRestriction: TableRestriction =
        (view.state.genomeView.featureTableRestriction as TableRestriction) || TableRestriction.REGION;
      // initialize the Variants table state so that we can set initial filters (to be applied to the Variants track as well)
      const parentId: number = view.state.genomeView.genome && view.state.genomeView.genome.id;
      const variantExpressionFilters: string = view.state.genomeView.variantExpressionFilters;
      if (variantExpressionFilters) {
        // either the variants or the vcf variants track must be switched on, so build the right tableSpec based on that
        let tableSpec: InputTableQuerySpec;
        if (view.state.genomeView.tracks.find((t) => t.type === TRACK_TYPE.VARIANT)) {
          tableSpec = createTableSpecForOrganismParent(ENTITY_TYPE.VARIANT, parentId);
        } else {
          tableSpec = createTableSpecForOrganismParent(ENTITY_TYPE.VCF_VARIANT, parentId);
        }

        newState = updateTableDataWithProps(newState, tableSpec, {
          filterExpression: variantExpressionFilters || "",
        });
      }
      // resurrect the state of the active table, if present
      if (view.state.genomeView.tableView) {
        newState = restoreFromView(newState, view.state.genomeView.tableView);
      }
      return {
        ...newState,
        rowRestriction: featureTableRestriction,
      };
    }
    return state;
  },

  [LOAD_TABLE_SUCCESS]: (
    state: ITableDataState,
    payload: { table: Table; tableSpec: InputTableQuerySpec; visibleColumns: string[] },
  ) => {
    const { totalRowCount, restrictedRowCount, filteredRowCount, templates } = payload.table;
    let columns = payload.table.columns.map((column) => parseTableColumnAndReplaceProjectTermWithSystemSetting(column));
    const visibleColumns: string[] = payload.visibleColumns;

    // If we enforce some columns to be visible at initialization (example from SearchResultPage) then we set the hidden flag to false for the corresponding columns
    if (visibleColumns) {
      columns = columns.map((column: ITableColumn) =>
        visibleColumns.find((vc: string) => vc === column.field)
          ? {
              ...column,
              hidden: false,
            }
          : column,
      );
    }

    const existingDataTable: ITableData = getTableDataSlice(state, payload.tableSpec);

    return updateTableDataWithProps(state, payload.tableSpec, {
      // isLoading: filteredRowCount > 0, // this would be good to let the table progress bar transition correctly from isLoadingMetadata to isLoading (progress bar should not start from 0) however because of some content browser initialization hassle we need to have { isLoading = false } for the rows fetching to start [SEL6-692]
      isLoadingTableMeta: false,
      initialColumns: columns,
      columns: columns,
      columnTemplates: templates,
      totalRowCount: totalRowCount,
      filteredRowCount: filteredRowCount,
      restrictedRowCount: restrictedRowCount,
      // rowIds: rowIds, // not needed since [SEL6-2271]
      selectionModel: existingDataTable ? existingDataTable.selectionModel : cloneSelectionModel(emptySelectionModel), // SEL6-2091 existingDataTable might not exist anymore if we navigated away at this stage
      tableQuerySpec: payload.tableSpec,
    });
  },

  [LOAD_TABLE_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec) => {
    return updateTableDataWithProps(state, tableSpec, {
      isLoadingTableMeta: false,
    });
  },

  [LOAD_TABLE_COLUMNS_SUCCESS]: (state: ITableDataState, payload: any) => {
    const columns: ITableColumn[] = payload.table.columns;
    return updateTableDataWithProps(state, payload.tableSpec, {
      isLoadingTableMeta: false,
      initialColumns: columns,
      columns: columns,
      columnTemplates: payload.table.templates,
    });
  },

  [LOAD_TABLE_COLUMNS_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec) => {
    return updateTableDataWithProps(state, tableSpec, {
      isLoadingTableMeta: false,
    });
  },

  [LOAD_ROWS_SUCCESS]: (state: ITableDataState, payload: { table: Table; tableSpec: InputTableQuerySpec }) => {
    const table: Table = payload.table;
    const tableSpec: InputTableQuerySpec = payload.tableSpec;
    const tableData: ITableData = getTableDataSlice(state, payload.tableSpec);

    // since rowIds and row count properties are optional we should eventually merge with the existing -cloned- props
    const rowIds: number[] = table.rowIds?.length ? table.rowIds.slice() : tableData.rowIds?.slice();
    const filteredRowCount: number = table.filteredRowCount;

    // note that endIndex is the first element PAST the loaded rows
    const endIndex: number = Math.min(tableSpec.pagination.offset + tableSpec.pagination.limit, filteredRowCount);

    // if by scrolling we get rows that have not been loaded in rowIds yet ...
    if (rowIds && (!rowIds[tableSpec.pagination.offset] || !rowIds[endIndex - 1])) {
      // ... then we add the identifiers of the rows to the rowIds prop
      for (let index = 0; index < table.pageRows.length; index++) {
        const pageRow: IIdentifiable = table.pageRows[index];
        if (pageRow.id) {
          rowIds[tableSpec.pagination.offset + index] = pageRow.id;
        }
      }
    }

    return updateTableDataWithProps(state, payload.tableSpec, {
      isLoading: false,
      pageRows: table.pageRows,
      filteredRowCount: filteredRowCount,
      rowIds: rowIds || [],
      selectionModel: tableData.selectionModel, // we try to sustain the selection model across restrictions ! change of ref genome should reset the selection model, this is done via a dedicated action (set_ref_genome)
      totalRowCount: table.totalRowCount,
      restrictedRowCount: table.restrictedRowCount,
      tableQuerySpec: payload.tableSpec,
    });
  },

  [LOAD_ROWS_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec) => {
    return updateTableDataWithProps(state, tableSpec, {
      isLoading: false,
    });
  },

  [SET_COLUMN_LIST]: (state: ITableDataState, payload) => {
    if (payload.resetRowIds) {
      return updateTableDataWithProps(state, payload.tableSpec, {
        columns: payload.orderedColumns,
        rowIds: [],
      });
    } else {
      return updateTableDataWithProps(state, payload.tableSpec, {
        columns: payload.orderedColumns,
      });
    }
  },

  [TRIGGER_TABLE_REFRESH]: (state: ITableDataState, payload) => {
    return updateTableDataWithProps(state, payload.tableSpec, {
      ...payload.props,
      rowIds: null,
    });
  },

  [SET_GLOBAL_FILTER]: (state: ITableDataState, payload: { tableSpec: InputTableQuerySpec; globalFilter: string }) => {
    return updateTableDataWithProps(state, payload.tableSpec, {
      filterExpression: payload.globalFilter,
    });
  },

  [SELECT_TABLE_ROW]: (
    state: ITableDataState,
    payload: {
      rowId: number;
      rowIndex: number;
      selected: boolean;
      tableSpec: InputTableQuerySpec;
      keepSelection: boolean;
    },
  ) => {
    const { tableSpec, selected, keepSelection } = payload;
    const tableData: ITableData = getTableDataSlice(state, tableSpec);
    const selectionModel: ISelectionModel = tableData.selectionModel;

    const newSelectionModel: ISelectionModel = keepSelection
      ? cloneSelectionModel(selectionModel)
      : cloneSelectionModel(emptySelectionModel);
    setSelected(newSelectionModel, payload.rowId, payload.rowIndex, selected);

    let newState: ITableDataState = updateTableDataWithProps(state, tableSpec, {
      selectionModel: newSelectionModel,
    });

    // if we don't keep the current selection we reset the selection model of all other tables
    if (!keepSelection) {
      Object.keys(newState.tables)
        .filter((key: string) => getTableDataKey(tableSpec) !== key)
        .map((key: string) => state.tables[key].tableQuerySpec)
        .forEach((otherTableSpec: InputTableQuerySpec) => {
          newState = updateTableDataWithProps(newState, otherTableSpec, {
            selectionModel: cloneSelectionModel(emptySelectionModel),
          });
        });
    }

    return newState;
  },

  [SELECT_TABLE_ROWS]: (state: ITableDataState, payload) => {
    const { tableSpec, ids, keepSelection, selected } = payload;
    const tableData: ITableData = getTableDataSlice(state, tableSpec);
    if (!tableData) {
      return state; // the table is not yet ready or does not exist any longer
    }
    const selectionModel: ISelectionModel = tableData.selectionModel;

    if (isEqual(selectionModel.selectedIds, ids) && selected) {
      return state;
    }

    const newSelectionModel: ISelectionModel = keepSelection
      ? cloneSelectionModel(selectionModel)
      : cloneSelectionModel(emptySelectionModel);
    setMultipleSelected(newSelectionModel, ids, selected);

    return updateTableDataWithProps(state, tableSpec, {
      selectionModel: newSelectionModel,
    });
  },

  [SELECT_TABLE_RANGE]: (state: ITableDataState, payload) => {
    const { tableSpec, rowIndex } = payload;
    const tableData: ITableData = getTableDataSlice(state, tableSpec);
    const selectionModel: ISelectionModel = tableData.selectionModel;

    const newSelectionModel: ISelectionModel = cloneSelectionModel(selectionModel);
    setRangeSelected(newSelectionModel, rowIndex, tableData.rowIds);

    return updateTableDataWithProps(state, tableSpec, {
      selectionModel: newSelectionModel,
    });
  },

  [SELECT_ALL_TABLE_ROWS]: (state: ITableDataState, payload) => {
    const { tableSpec } = payload;
    const tableData: ITableData = getTableDataSlice(state, tableSpec);

    const newSelectionModel = cloneSelectionModel(emptySelectionModel);
    setAllSelected(newSelectionModel, tableData.rowIds, true);

    return updateTableDataWithProps(state, tableSpec, {
      selectionModel: newSelectionModel,
    });
  },

  [DESELECT_ALL_TABLE_ROWS]: (state: ITableDataState, payload) => {
    const { tableSpec } = payload;

    return updateTableDataWithProps(state, tableSpec, {
      selectionModel: cloneSelectionModel(emptySelectionModel),
    });
  },

  [RESET_DATA_TABLE]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    const tableDataKey: string = getTableDataKey(tableSpec);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [tableDataKey]: tableData, ...others } = state.tables;
    return {
      ...state,
      tables: others,
    };
  },

  [SELECT_TABLE]: (state: ITableDataState, payload: InputTableQuerySpec): ITableDataState => {
    return {
      ...state,
      activeTable: payload,
    };
  },

  [DESTROY_ACTIVE_TABLE]: (state: ITableDataState, payload: InputTableQuerySpec): ITableDataState => {
    if (sameTableSpec(state.activeTable, payload)) {
      return {
        ...state,
        activeTable: null,
      };
    }
    return state;
  },

  [FETCH_COLUMN_TEMPLATES_REQUEST]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, true);
  },

  [FETCH_COLUMN_TEMPLATES_SUCCESS]: (state: ITableDataState, payload: any): ITableDataState => {
    const { templates, tableSpec } = payload;
    return updateTableDataWithProps(state, tableSpec, {
      isLoadingTemplates: false,
      columnTemplates: templates,
    });
  },

  [FETCH_COLUMN_TEMPLATES_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [DELETE_COLUMN_TEMPLATE_REQUEST]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, true);
  },

  [DELETE_COLUMN_TEMPLATE_SUCCESS]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [DELETE_COLUMN_TEMPLATE_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [SAVE_COLUMN_TEMPLATE_REQUEST]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, true);
  },

  [SAVE_COLUMN_TEMPLATE_SUCCESS]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [SAVE_COLUMN_TEMPLATE_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [UPDATE_COLUMN_TEMPLATE_REQUEST]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, true);
  },

  [UPDATE_COLUMN_TEMPLATE_SUCCESS]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [UPDATE_COLUMN_TEMPLATE_FAILURE]: (state: ITableDataState, tableSpec: InputTableQuerySpec): ITableDataState => {
    return setIsLoadingTemplatesFlag(state, tableSpec, false);
  },

  [UPDATE_TABLE_GUIDANCE_STATE]: (state: ITableDataState, doNotShowPerfGuidance: boolean): ITableDataState => {
    return {
      ...state,
      doNotShowPerfGuidance: doNotShowPerfGuidance,
    };
  },
  [SET_EXPANSION_PATH]: (state: ITableDataState, payload: any): ITableDataState => {
    const { tableQuerySpec, expansionPath } = payload;
    return updateTableDataWithProps(state, tableQuerySpec, {
      expansionPath: expansionPath,
    });
  },
  [ENSURE_ROW_VISIBLE]: (state: ITableDataState, payload: any): ITableDataState => {
    const { tableQuerySpec, rowId, rowIndex } = payload;
    return updateTableDataWithProps(state, tableQuerySpec, {
      scrollToRowId: rowId,
      scrollToRowIndex: rowIndex,
    });
  },
  [CHANGE_TABLE_RESTRICTION]: (state: ITableDataState, rowRestriction: TableRestriction): ITableDataState => {
    return {
      ...state,
      rowRestriction: rowRestriction,
    };
  },

  [UPDATE_TABLE_ROW]: (
    state: ITableDataState,
    payload: { tableQuerySpec: InputTableQuerySpec; rowData: IIdentifiable },
  ): ITableDataState => {
    return updateTableDataWithProps(state, payload.tableQuerySpec, {
      rowToUpdate: payload.rowData,
    });
  },

  [SET_REFERENCE_GENOME]: (state: ITableDataState): ITableDataState => {
    // [SEL6-2150, SEL6-2271] when changing the ref genome the selection model needs to be reset because the row ids are totally different and existing selection model would not apply any longer
    let newState: ITableDataState = state;
    Object.keys(state.tables)
      .map((key: string) => state.tables[key])
      .filter((tableData: ITableData) => tableData.tableQuerySpec.restrictions?.refGenomeId)
      .forEach((tableData: ITableData) => {
        // [SEL6-2274] if a variant profile filter is set and the reference genome is changed then we should reset this specific filter since it MIGHT not makes sense (as discussed with Michael Z.)
        const filteredVariantProfileColumn = tableData.columns?.find(
          (column: ITableColumn) => column.cellType === CELL_TYPE.VARIANT_PROFILE && column.filterExpression,
        );
        const newColumnsWithoutVariantProfileFilter: ITableColumn[] = filteredVariantProfileColumn
          ? tableData.columns.map((column) =>
              column.columnId === filteredVariantProfileColumn.columnId
                ? {
                    ...column,
                    filterExpression: null,
                  }
                : column,
            )
          : tableData.columns;

        newState = updateTableDataWithProps(newState, tableData.tableQuerySpec, {
          selectionModel: cloneSelectionModel(emptySelectionModel),
          columns: newColumnsWithoutVariantProfileFilter,
          filterExpression: getFilterExpression(newColumnsWithoutVariantProfileFilter) || "",
        });
      });

    return newState;
  },
});

/**
 * Initialize the specified table slice properties of the tableData redux state
 * @param tableSpec the object representing the table slice to eb initialized
 * @param state the current TableData redux state
 * @returns {ITableDataState} the new state
 */
export function initializeTableDataIfNeeded(tableSpec: InputTableQuerySpec, state: ITableDataState): ITableDataState {
  const tableData: ITableData = getTableDataSlice(state, tableSpec);
  if (!tableData || !sameTableSpec(tableData.tableQuerySpec, tableSpec)) {
    const tableDataKey: string = getTableDataKey(tableSpec);

    return {
      ...state,
      tables: {
        ...state.tables,
        [tableDataKey]: {
          ...initialTableData,
          tableQuerySpec: tableSpec,
        },
      },
    };
  }
  return state;
}

export function updateTableDataWithProps(
  state: ITableDataState,
  tableSpec: InputTableQuerySpec,
  props: Partial<ITableData>,
): ITableDataState {
  const preparedState: ITableDataState = initializeTableDataIfNeeded(tableSpec, state);
  const tableDataKey: string = getTableDataKey(tableSpec);
  return {
    ...preparedState,
    tables: {
      ...preparedState.tables,
      [tableDataKey]: {
        ...preparedState.tables[tableDataKey],
        ...pickBy(props, (v) => v !== undefined), // clean up the object from undefined values (null should be a valid value!), so that prepared state takes precedence for undefined props
      },
    },
  };
}

/**
 * Initialize the tableData redux state for all group types available in the search result
 * @param searchResultData the object holding the search result groups
 * @param state the current TableData redux state
 * @returns {ITableDataState} the new state
 */
export function getInitialTableDataStateForSearchResult(
  searchResultData: SearchResult,
  state: ITableDataState,
): ITableDataState {
  let newState: ITableDataState = state;
  searchResultData.groups.forEach((group: Group) => {
    const tableSpec: InputTableQuerySpec = createTableSpecForGroupParent(group.type, group.itemSubTypeId);
    // set the isLoading flag to true
    newState = updateTableDataWithProps(state, tableSpec, {
      isLoadingTableMeta: true,
    });
  });
  return newState;
}

export function createTableColumnsWithSort(
  columns: ITableColumn[],
  columnField: string,
  sortOrder: string,
  multiSort: boolean,
): ITableColumn[] {
  const nextPriority: number =
    columns.filter((element: ITableColumn) => {
      return !!element.sort;
    }).length + 1;
  let removedPriority: number = null;

  // record the selected sort order in the sorted column
  const newColumnsWithSort: ITableColumn[] = columns.map((col) => {
    const newCol: ITableColumn = { ...col };
    if (multiSort) {
      if (col.field === columnField) {
        if (sortOrder !== null) {
          newCol.sort = { direction: sortOrder, priority: col.sort ? col.sort.priority : nextPriority };
        } else {
          removedPriority = newCol.sort.priority;
          delete newCol.sort;
        }
      }
    } else {
      if (col.field === columnField && sortOrder !== null) {
        newCol.sort = { direction: sortOrder, priority: 1 };
      } else {
        delete newCol.sort;
      }
    }

    return newCol;
  });

  // clean up priorities from gaps before returning
  if (removedPriority) {
    for (const col of newColumnsWithSort) {
      if (col.sort && col.sort.priority > removedPriority) {
        col.sort.priority--;
      }
    }
  }
  return newColumnsWithSort;
}

export function applyMultiColumnSort(tableState: ITableData, sortedColumns: ITableColumn[]) {
  const sortedMap = keyBy(sortedColumns, "field");
  // key: field, data: column

  // record the selected sort order in the sorted column
  const newColumnsWithSort: ITableColumn[] = tableState.columns.map((col) => {
    const newCol: ITableColumn = { ...col };
    const currCol = sortedMap[col.field];
    if (currCol) {
      newCol.sort = currCol.sort;
    } else {
      delete newCol.sort;
    }

    return newCol;
  });

  return newColumnsWithSort;
}

export function sameTableQuerySpecRestrictions(
  tableRestriction1: InputRestriction | null | undefined,
  tableRestriction2: InputRestriction | null | undefined,
) {
  return JSON.stringify(tableRestriction1) === JSON.stringify(tableRestriction2);
}

export function sameTableSpec(
  tableSpec1: InputTableQuerySpec | null | undefined,
  tableSpec2: InputTableQuerySpec | null | undefined,
) {
  if (!tableSpec1 || !tableSpec2) {
    return false;
  }

  // CAUTION: if tableSpec comes from the server (e.g. as part of a saved view) the value might be null instead of undefined, in which case we cannot compare with ===
  // undefined == null -> true
  return (
    tableSpec1.queryType == tableSpec2.queryType &&
    tableSpec1.resultType == tableSpec2.resultType &&
    tableSpec1.id == tableSpec2.id &&
    // && (!compareRefGenome || sameTableQuerySpecRestriction(tableSpec1, tableSpec2)) // otherwise the change of Reference Genome in the Genome Viewer will not trigger a refresh of the features table e.g. [SEL5-2186] EDIT: this is now done via the data table component key
    tableSpec1.resultSubTypeId == tableSpec2.resultSubTypeId
  );
}

export function isTableDataInitialized(tableData: ITableData) {
  return tableData.totalRowCount > -1;
}

function setIsLoadingTemplatesFlag(
  state: ITableDataState,
  tableSpec: InputTableQuerySpec,
  isLoadingTemplates: boolean,
) {
  return updateTableDataWithProps(state, tableSpec, {
    isLoadingTemplates: isLoadingTemplates,
  });
}

export interface ITableInitializationOptions {
  visibleColumns?: string[];
  sortExpression?: string;
  filterExpression?: string;
  initialSelectionModel?: ISelectionModel;
}

/**
 * Action creators
 */
export function loadTable(
  tableSpec: ITableSpecWithContext | null | undefined,
  options?: ITableInitializationOptions,
): ReduxAction<Promise<unknown>> {
  return (dispatch, getState, webApiClient) => {
    if (tableSpec == undefined) {
      return createErrorHandler(dispatch, {
        actionType: LOAD_TABLE_FAILURE,
        actionPayload: tableSpec,
        alertTitle: "Missing Table Specifications",
        showAlert: true,
      })({
        name: "Load Table",
        message: "The table specifications are undefined.",
        graphQLErrors: [],
        protocolErrors: [],
        clientErrors: [],
        networkError: null,
        extraInfo: undefined,
      });
    }

    const state: IAppState = getState();
    const tableData = state.tableData.tables[getTableDataKey(tableSpec)]; // because the Array index may not exists

    // make sure to not load again the same table
    if (
      tableData &&
      sameTableSpec(tableData.tableQuerySpec, tableSpec) &&
      sameTableQuerySpecRestrictions(tableData.tableQuerySpec?.restrictions, tableSpec.restrictions) &&
      // and if the table has been initialized but no data requested
      isTableDataInitialized(tableData)
    ) {
      return Promise.resolve(tableData);
    }

    // columns were empty but the table slice is already there, maybe with some useful information already (like when a view is loaded)
    const optionFilterExpression = options?.filterExpression;
    const existingFilterExpression: string =
      optionFilterExpression ??
      (tableData && sameTableSpec(tableData.tableQuerySpec, tableSpec) ? tableData.filterExpression : "");

    dispatch({
      type: LOAD_TABLE_REQUEST,
      payload: {
        tableSpec,
        filterExpression: existingFilterExpression,
        selectionModel: options?.initialSelectionModel,
      },
    });

    const tableQuerySpec: IInputTableQuerySpecWithContext = {
      ...tableSpec,
      rowFilter: existingFilterExpression, // SEL5-1326
      rowSorting: options?.sortExpression ?? null,
      context: tableSpec.context, // not sure, perhaps already there
    };

    const queryParams: IDataTableQueryParams = {
      tableQuerySpec,
      withColumns: true,
      // withRowIds: true, // not required at this stage since [SEL6-2271]
      withPagination: true,
    };
    return webApiClient
      .fetchTableData(queryParams)
      .then((table: Table) => {
        dispatch({
          type: LOAD_TABLE_SUCCESS,
          payload: {
            table,
            tableSpec: tableQuerySpec,
            visibleColumns: options?.visibleColumns,
          },
        });
        return table;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TABLE_FAILURE,
          actionPayload: tableSpec,
          alertTitle: "Fetching of Table Data Failed",
          throwError: true,
        }),
      );
  };
}

// just loading the table columns definition in order to get our hand on shared things like filters, ...
export function loadTableColumns(tableSpec: InputTableQuerySpec, force = false) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IAppState = getState();
    const tableData: ITableData = state.tableData.tables[getTableDataKey(tableSpec)];

    // make sure to not load again the table columns for the same table...
    if (
      !force &&
      tableData &&
      sameTableSpec(tableData.tableQuerySpec, tableSpec) &&
      //  ...if the table has been initialized but no data requested
      tableData.columns.length > 0
    ) {
      return;
    }

    // columns were empty but the table slice is already there, maybe with some useful information already (like when a view is loaded)
    let initialFilterExpression: string = tableSpec.rowFilter;
    if (tableData && sameTableSpec(tableData.tableQuerySpec, tableSpec)) {
      initialFilterExpression = tableData.filterExpression;
    }

    dispatch({
      type: LOAD_TABLE_COLUMNS_REQUEST,
      payload: {
        tableSpec,
        filterExpression: initialFilterExpression,
      },
    });

    const queryParams: IDataTableQueryParams = {
      tableQuerySpec: tableSpec,
      withColumns: true,
    };

    return webApiClient
      .fetchTableData(queryParams)
      .then((table: Table) => {
        // In case the LOAD_TABLE start after this request starts and completes before this request completes,
        // we don't want to overwrite things like the column filters
        if (!force && getState().tableData.tables[getTableDataKey(tableSpec)]?.columns.length > 0) {
          return;
        }

        // keep the existing filtering and sort information wherever possible upon columns refresh
        const columns: Column[] = table.columns.map((col) => {
          const existingColumnDefinition = tableData?.columns.find((c) => c.columnId === col.columnId);
          return existingColumnDefinition
            ? ({
                ...col,
                filterExpression: existingColumnDefinition.filterExpression,
                sort: existingColumnDefinition.sort,
              } as Column)
            : col;
        });
        dispatch({
          type: LOAD_TABLE_COLUMNS_SUCCESS,
          payload: {
            table: {
              ...table,
              columns: columns,
            },
            tableSpec: tableSpec,
          },
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TABLE_COLUMNS_FAILURE,
          actionPayload: tableSpec,
          alertTitle: "Fetching of Table Columns Failed",
        }),
      );
  };
}

export function getSortOrderExpressionFromTableColumns(columns: Partial<ITableColumn>[]): string {
  // create a tmp column list, ordered by sorting priority
  const orderedCols = sortBy(columns, "sort.priority");

  // concatenate the fields of the columns, as ordered before
  return orderedCols.reduce((prev: string, curr: ITableColumn) => {
    if (!curr.sort) {
      return prev;
    } else {
      // add the new sort token to the total string
      return (
        prev +
        (prev ? "," : "") +
        (curr.sort.direction.toLowerCase() === SORT_ORDER_DESC.toLowerCase() ? "-" : "") +
        curr.field
      );
    }
  }, "");
}

export function buildDataTableQueryParamsForSpecificColumns(
  tableQuerySpec: InputTableQuerySpec,
  columns: ITableColumn[],
): IDataTableQueryParams {
  const entityType: string = tableQuerySpec.resultType;
  return {
    tableQuerySpec,
    withTranscripts: isColumnVisible(
      [
        ENTITY_TYPE.GENE,
        ENTITY_TYPE.GENE_MUTATION,
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
      ],
      "transcripts",
      entityType,
      columns,
    ),
    withProteins: isColumnVisible(
      [ENTITY_TYPE.GENE, ENTITY_TYPE.TRANSCRIPT, ENTITY_TYPE.TRANSCRIPT_LOCATION, ENTITY_TYPE.GENE_MUTATION],
      "proteins",
      entityType,
      columns,
    ),
    withGenes: isColumnVisible(
      [
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.TRANSCRIPT,
        ENTITY_TYPE.TRANSCRIPT_LOCATION,
      ],
      "genes",
      entityType,
      columns,
    ),
    withTranscriptLocations: isColumnVisible(ENTITY_TYPE.TRANSCRIPT, "transcriptLocations", entityType, columns),
    withProteinLocations: isColumnVisible(
      [ENTITY_TYPE.PROTEIN, ENTITY_TYPE.ORTHOLOG, ENTITY_TYPE.DIRECTIONAL_ORTHOLOG, ENTITY_TYPE.PROTEIN_MUTATION],
      "proteinLocations",
      entityType,
      columns,
    ),
    withGenomeStatistics: isColumnVisible(
      [ENTITY_TYPE.ORGANISM, ENTITY_TYPE.SAMPLE],
      "genomeStatistics",
      entityType,
      columns,
    ),
    withGeneMutations: isColumnVisible(
      [ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION, ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT],
      "geneMutations",
      entityType,
      columns,
    ),
    // [SEL6-2102] split the graphQL fragment matcher into more specific "field" pieces
    withProteinMutations:
      isColumnVisible([ENTITY_TYPE.MUTATION], "refVariant.proteinMutations", entityType, columns) ||
      isColumnVisible([ENTITY_TYPE.VARIANT, ENTITY_TYPE.GENE_MUTATION], "proteinMutations", entityType, columns),
    // the following makes loading the variant data somewhat more efficient, but needs the above split (otherwise there are side-effects because when the "variant proteins" col is switched on, the "withProteinMutations" flag on ref side is set as well)
    withMutProteinMutations: isColumnVisible(
      [ENTITY_TYPE.MUTATION],
      "mutVariants.proteinMutations",
      entityType,
      columns,
    ),
    withRefVariant: isColumnVisible(
      [ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION, ENTITY_TYPE.VARIANT],
      "refVariant",
      entityType,
      columns,
    ),
    withRefRegion: isColumnVisible(
      [ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION, ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT],
      "refRegion",
      entityType,
      columns,
    ),
    withAnalysisGroup: isColumnVisible(
      [ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION, ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT],
      "variantAnalysisGroup",
      entityType,
      columns,
    ),
    withSequence: isColumnVisible(
      [ENTITY_TYPE.BLAST_HIT, ENTITY_TYPE.BLAST_HSP],
      [
        BLAST_HITS_TABLE_COLUMNS.SUBJECT_SEQUENCE,
        BLAST_HITS_TABLE_COLUMNS.ALIGNED_SUBJECT_SEQUENCE,
        BLAST_HITS_TABLE_COLUMNS.HSP_TARGET_SEQUENCE,
        BLAST_HITS_TABLE_COLUMNS.HSP_GENE_SEQUENCE,
        BLAST_HITS_TABLE_COLUMNS.OVERLAPPING_GENE_SEQUENCE,
      ],
      entityType,
      columns,
    ),
    withLiteratureReferences: isColumnVisible(
      [
        ENTITY_TYPE.GENE,
        ENTITY_TYPE.GENE_MUTATION,
        ENTITY_TYPE.TRANSCRIPT,
        ENTITY_TYPE.TRANSCRIPT_LOCATION,
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.ORGANISM,
        ENTITY_TYPE.SAMPLE,
        ENTITY_TYPE.PROTEIN_MUTATION,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
      ],
      ["literatureReferences"],
      entityType,
      columns,
    ),
    withOtherReferences: isColumnVisible(
      [
        ENTITY_TYPE.GENE,
        ENTITY_TYPE.GENE_MUTATION,
        ENTITY_TYPE.TRANSCRIPT,
        ENTITY_TYPE.TRANSCRIPT_LOCATION,
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.ORGANISM,
        ENTITY_TYPE.SAMPLE,
        ENTITY_TYPE.PROTEIN_MUTATION,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
      ],
      ["externalReferences"],
      entityType,
      columns,
    ),
    withPFam: isColumnVisible(
      [
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.PROTEIN_MUTATION,
      ],
      "pfamMotifs",
      entityType,
      columns,
    ),
    withInternalReferences: isColumnVisible(
      [
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.TRANSCRIPT,
        ENTITY_TYPE.TRANSCRIPT_LOCATION,
        ENTITY_TYPE.CONTIG,
        ENTITY_TYPE.GENE_MUTATION,
        ENTITY_TYPE.PROTEIN_MUTATION,
      ],
      "internalReferences",
      entityType,
      columns,
    ),
    withPathways: isColumnVisible(
      [
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.PROTEIN_MUTATION,
      ],
      "pathways",
      entityType,
      columns,
    ),
    withLowerNeighborGenes: isColumnVisible(
      [ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT, ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION],
      "lowerGenes",
      entityType,
      columns,
    ),
    withHigherNeighborGenes: isColumnVisible(
      [ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT, ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION],
      "higherGenes",
      entityType,
      columns,
    ),
    withLowerNeighborVariants: isColumnVisible(
      [ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT, ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION],
      "lowerVariants",
      entityType,
      columns,
    ),
    withHigherNeighborVariants: isColumnVisible(
      [ENTITY_TYPE.MUTATION, ENTITY_TYPE.VARIANT, ENTITY_TYPE.GENE_MUTATION, ENTITY_TYPE.PROTEIN_MUTATION],
      "higherVariants",
      entityType,
      columns,
    ),
    withBlastResults: isColumnVisible(
      [
        ENTITY_TYPE.PROTEIN,
        ENTITY_TYPE.ORTHOLOG,
        ENTITY_TYPE.DIRECTIONAL_ORTHOLOG,
        ENTITY_TYPE.PROTEIN_LOCATION,
        ENTITY_TYPE.PROTEIN_MUTATION,
      ],
      "bestBlastResults",
      entityType,
      columns,
    ),
    withMutVariants: isColumnVisible([ENTITY_TYPE.MUTATION], "mutVariants", entityType, columns),
    withSamples: isColumnVisible([ENTITY_TYPE.ORGANISM], "sample", entityType, columns),
  };
}

/**
 *
 * @param start
 * @param end
 * @param tableSpec
 * @param newColumns any new columns with new filter or sorting information to be applied when rows are loaded
 * @returns {(dispatch:any, getState:any, webApiClient:WebApiClient)=>Promise<Table>}
 */
const lastQueryParams: any = {};

export function loadTableRows(start: number, end: number, tableSpec: ITableSpecWithContext) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Table> => {
    const state: IAppState = getState();
    const tableData: ITableData = state.tableData.tables[getTableDataKey(tableSpec)];
    const uniqueKeyTableSpec: string = createUniqueKeyFromTableSpec(tableSpec);
    const columns = tableData && tableData.columns;

    if (!columns?.length) {
      return Promise.resolve(null); // might happen when component is unmounting
    }

    const tableQuerySpec: IInputTableQuerySpecWithContext = {
      ...tableSpec,
      pagination: {
        // note that end is exclusive, so no "-1" correction is required
        limit: end - start,
        offset: start,
      },
      rowFilter: tableData.filterExpression,
      rowSorting: getSortOrderExpressionFromTableColumns(columns),
      context: tableSpec.context,
    };

    dispatch({ type: LOAD_ROWS_REQUEST, payload: tableSpec });

    // The list of row ids and counts have to be updated whenever the overall rows do change! otherwise the "Select all" (making use of the rowIds) will be wrong
    // Trigger also when e.g. sample editing was done, rowIds is reset when saving the data from sample editing.
    const refreshRowsIdsAndCounts: boolean =
      !tableData.rowIds?.length || // rowIds are reset usually when filtering/sorting/columns do change
      !sameTableQuerySpecRestrictions(tableSpec.restrictions, tableData.tableQuerySpec.restrictions);

    const queryParams: IDataTableQueryParams = Object.assign(
      buildDataTableQueryParamsForSpecificColumns(tableQuerySpec, columns),
      {
        withColumns: false,
        withRowIds: refreshRowsIdsAndCounts,
        withPageRows: true,
        withPagination: refreshRowsIdsAndCounts,
      },
    );
    // make sure the last request is the last loaded one, the previous ones will be ignored
    lastQueryParams[uniqueKeyTableSpec] = queryParams;

    return webApiClient
      .fetchTableData(queryParams, columns)
      .then((table: Table) => {
        if (lastQueryParams[uniqueKeyTableSpec] === queryParams && getTableDataSlice(getState().tableData, tableSpec)) {
          // if the table has been actively destroyed in between
          dispatch({
            type: LOAD_ROWS_SUCCESS,
            payload: {
              table,
              tableSpec: tableQuerySpec,
            },
          });
          // the ag-grid API needs the counts, otherwise it tries to load additional rows (infinite scrolling)
          return {
            ...table,
            filteredRowCount: table.filteredRowCount != undefined ? table.filteredRowCount : tableData.filteredRowCount,
          };
        } else {
          dispatch({ type: LOAD_ROWS_IGNORE });
          return null;
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ROWS_FAILURE,
          actionPayload: tableSpec,
          alertTitle: "Fetching of Data Table Rows Failed",
        }),
      );
  };
}

export function isColumnVisible(
  queryEntityType: string | string[],
  fieldPrefix: string | string[],
  tableEntityType: string,
  columns: ITableColumn[],
): boolean {
  if (
    (Array.isArray(queryEntityType) && !queryEntityType.find((qe) => qe === tableEntityType)) ||
    (!Array.isArray(queryEntityType) && queryEntityType !== tableEntityType)
  ) {
    // No need to look for columns if page row entity doesn't match
    return false;
  }

  return !!columns
    .filter((col) => !col.hidden)
    .find((col) => {
      if (Array.isArray(fieldPrefix)) {
        return !!fieldPrefix.find(
          (f: string) => col.field === f || col.field.startsWith(`${f}.`) || col.field.includes(`.${f}.`),
        );
      } else {
        return (
          col.field === fieldPrefix || col.field.startsWith(`${fieldPrefix}.`) || col.field.includes(`.${fieldPrefix}.`)
        );
      }
    });
}

export function initDataTables(tableSpecs: InputTableQuerySpec[]) {
  return (dispatch, _getState) => {
    tableSpecs.forEach((tableSpec: InputTableQuerySpec) => dispatch(loadTableColumns(tableSpec)));
  };
}

export function setOrderedColumnList(
  orderedColumns: ITableColumn[],
  tableSpec: InputTableQuerySpec,
): ReduxAction<Promise<boolean>> {
  return (dispatch, _getState, _webApiClient) => {
    dispatch(setTableColumns(orderedColumns, tableSpec));

    return dispatch(updateActiveTemplate(orderedColumns, tableSpec));
  };
}

export function setTableColumns(orderedColumns: ITableColumn[], tableSpec: InputTableQuerySpec, resetRowIds = false) {
  return (dispatch, _getState, _webApiClient: WebApiClient): Promise<boolean> => {
    return dispatch({
      type: SET_COLUMN_LIST,
      payload: {
        orderedColumns: orderedColumns,
        tableSpec: tableSpec,
        resetRowIds: resetRowIds,
      },
    });
  };
}

export function triggerTableRefresh(tableSpec: InputTableQuerySpec, props?: Partial<ITableData>) {
  return {
    type: TRIGGER_TABLE_REFRESH,
    payload: {
      tableSpec: tableSpec,
      props: props,
    },
  };
}

export function updateActiveTemplate(orderedColumns: ITableColumn[], tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    // synchronize the current column setting into the column template in the database
    const columns: InputColumnInput[] = orderedColumns.map((c: ITableColumn) => {
      const { columnId, namespace, hidden } = c;
      return {
        columnId: columnId,
        namespace: namespace,
        hidden: hidden,
      };
    });

    return webApiClient.updateActiveTemplate(columns, tableSpec).catch(
      createErrorHandler(dispatch, {
        actionType: SYNC_COLUMN_LIST_FAILURE,
        actionPayload: tableSpec,
        alertTitle: "Updating Columns of Active Template Failed",
      }),
    );
  };
}

export function reloadTableRows(tableSpec: InputTableQuerySpec, tableUpdated = false) {
  return (dispatch, getState) => {
    const tableData: ITableData = getTableDataSlice(getState().tableData, tableSpec);

    // simply trigger the reload of the data (keeping the current scroll position) by setting the same set of columns again
    dispatch(setTableColumns(tableData.columns.slice(0), tableSpec, tableUpdated));
  };
}

export function sortByTableColumn(
  columnField: string,
  sortOrder: string,
  multiSort: boolean,
  tableSpec: InputTableQuerySpec,
): ActionCreator<Promise<Table>> {
  return (dispatch, getState): Promise<Table> => {
    const tableData: ITableData = getTableDataSlice(getState().tableData, tableSpec);
    const newColumnsWithSort: ITableColumn[] = createTableColumnsWithSort(
      tableData.columns,
      columnField,
      sortOrder,
      multiSort,
    );

    return dispatch(setTableColumns(newColumnsWithSort, tableSpec, true));
  };
}

export function sortByMultipleTableColumns(
  sortedColumns: ITableColumn[],
  tableSpec: InputTableQuerySpec,
): ActionCreator<Promise<Table>> {
  return (dispatch, getState): Promise<Table> => {
    const tableData: ITableData = getTableDataSlice(getState().tableData, tableSpec);
    const newColumnsWithSort: ITableColumn[] = applyMultiColumnSort(tableData, sortedColumns);

    return dispatch(setTableColumns(newColumnsWithSort, tableSpec, true));
  };
}

export function filterByTableColumn(filterPayload: IFilterPayload | IFilterPayload[], removePreviousFilters = false) {
  return (dispatch, getState) => {
    const filters: IFilterPayload[] = Array.isArray(filterPayload) ? filterPayload : [filterPayload];

    // we assume all filters are targeting the same tableSpec
    const tableSpec: InputTableQuerySpec = filters[0] && filters[0].tableSpec;
    const stateTableData = getState().tableData;
    const tableData: ITableData = getTableDataSlice(stateTableData, tableSpec);

    // loop through the table columns
    const tableColumns: ITableColumn[] = tableData?.columns.map((col: ITableColumn) => {
      const columnFilter: IFilterPayload = filters.find((filter: IFilterPayload) => filter.columnField === col.field);
      if (columnFilter) {
        dispatch(setFilterToTableColumn(columnFilter)); // useful for content browser module only
        return {
          ...col,
          filterExpression: columnFilter.filterExpression,
          hidden: false,
        };
      } else if (removePreviousFilters && col.filterExpression) {
        // prevent combining with previous filters
        return { ...col, filterExpression: null };
      } else {
        return col;
      }
    });

    const globalFilter: string = getFilterExpression(tableColumns) || "";
    dispatch(setGlobalFilterExpression(tableSpec, globalFilter));

    return dispatch(setTableColumns(tableColumns, tableSpec, true));
  };
}

export function setGlobalFilterExpression(tableSpec: InputTableQuerySpec, globalFilter: string) {
  return {
    type: SET_GLOBAL_FILTER,
    payload: {
      tableSpec,
      globalFilter,
    },
  };
}

export function setFilterToTableColumn(filterPayload: IFilterPayload) {
  // This action is ignored by the table-data reducer but needed in the content-browser reducer
  return {
    type: FILTER_BY_TABLE_COLUMN,
    payload: filterPayload,
  };
}

export function setColumnsVisibility(columns: ITableColumn[], hidden: boolean, tableSpec: InputTableQuerySpec) {
  return (dispatch, getState, _webApiClient: WebApiClient): Promise<boolean> => {
    const tableData: ITableData = getTableDataSlice(getState().tableData, tableSpec);

    const columnFields = columns.map((col) => col.field);
    const newColumns: ITableColumn[] = tableData.columns.map((col: ITableColumn) =>
      columnFields.includes(col.field) ? { ...col, hidden: hidden } : col,
    );
    if (!hidden) {
      columns.forEach((column: ITableColumn) => {
        if (!tableData.columns.find((existingColumn) => existingColumn.field === column.field)) {
          // case where a new column has been created/added, since it does not exist in the column definition yet
          newColumns.push(column);
        }
      });
    }

    dispatch(setTableColumns(newColumns, tableSpec));

    return dispatch(updateActiveTemplate(newColumns, tableSpec));
  };
}

export function resetAllFiltersAndReloadTable(tableSpec: InputTableQuerySpec) {
  return (dispatch, getState) => {
    const tableData: ITableData = getTableDataSlice(getState().tableData, tableSpec);

    dispatch(resetAllFilters(tableSpec)); // only for content browser

    const newColumnsWithFilter: ITableColumn[] = tableData.columns.map((col: ITableColumn) =>
      col.filterExpression
        ? {
            ...col,
            filterExpression: null,
          }
        : col,
    );

    const globalFilter: string = getFilterExpression(newColumnsWithFilter) || "";
    dispatch(setGlobalFilterExpression(tableSpec, globalFilter));

    return dispatch(setTableColumns(newColumnsWithFilter, tableSpec, true));
  };
}

export function resetAllFilters(tableSpec: InputTableQuerySpec) {
  // This action is ignored by the table-data reducer but needed in the content-browser reducer
  return {
    type: RESET_FILTER,
    payload: {
      tableSpec,
    },
  };
}

export function selectTableRow(
  rowId: number,
  rowIndex: number,
  selected: boolean,
  tableSpec: InputTableQuerySpec,
  keepSelection: boolean,
) {
  return {
    type: SELECT_TABLE_ROW,
    payload: {
      rowId,
      rowIndex,
      tableSpec,
      selected,
      keepSelection,
    },
  };
}

export function selectTableRows(
  tableSpec: InputTableQuerySpec,
  ids: TGridSelModelIdentifier[],
  selected: boolean,
  keepSelection: boolean,
) {
  return {
    type: SELECT_TABLE_ROWS,
    payload: {
      tableSpec,
      ids,
      selected,
      keepSelection,
    },
  };
}

export function selectTableRange(rowIndex: number, tableSpec: InputTableQuerySpec) {
  return (dispatch, getState) => {
    const state: IAppState = getState();

    // first check the availability of all rows to be selected
    const tableData: ITableData = getTableDataSlice(state.tableData, tableSpec);
    const selectionModel: ISelectionModel = tableData.selectionModel;
    const { rangeStart, rangeStop } = computeSelectionRange(selectionModel, rowIndex);
    const allIdsAvailable: boolean = checkAvailabilityOfIdentifiers(tableData.rowIds, rangeStart, rangeStop);
    if (!allIdsAvailable) {
      notify.warning({
        title: selMessage({ id: "table.select.warningTitle" }),
        children: selMessage({ id: "table.select.TableRange.warningMessage" }),
      });
    } else {
      dispatch({
        type: SELECT_TABLE_RANGE,
        payload: {
          rowIndex,
          tableSpec,
        },
      });
    }
  };
}

export function toggleSelection(selected = true, tableSpec: InputTableQuerySpec) {
  return (dispatch, getState) => {
    const state: IAppState = getState();

    if (selected === true) {
      // first check the availability of all rows to be selected
      const tableData: ITableData = getTableDataSlice(state.tableData, tableSpec);
      const allIdsAvailable: boolean = checkAvailabilityOfIdentifiers(
        tableData.rowIds,
        0,
        tableData.filteredRowCount - 1,
      );
      if (!allIdsAvailable) {
        const maxGridSelection = state.config.configuration.application.maxSelectedTableRows;
        notify.warning({
          title: selMessage({ id: "table.select.warningTitle" }),
          children: selMessage({
            id: "table.select.toggle.warningMessage",
            values: { maxGridSelection: maxGridSelection },
          }),
        });
        return;
      }
    }

    dispatch({
      type: selected ? SELECT_ALL_TABLE_ROWS : DESELECT_ALL_TABLE_ROWS,
      payload: {
        tableSpec,
      },
    });
  };
}

export function selectTable(tableSpec: InputTableQuerySpec) {
  return {
    type: SELECT_TABLE,
    payload: tableSpec,
  };
}

export function fetchRowPosition(targetId: number, tableSpec: InputTableQuerySpec) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<number> => {
    const state: IAppState = getState();

    const tableData: ITableData = getTableDataSlice(state.tableData, tableSpec);

    const tableQuerySpec: InputTableQuerySpec = {
      ...tableData.tableQuerySpec,
      rowFilter: getFilterExpression(tableData.columns),
      rowSorting: getSortOrderExpressionFromTableColumns(tableData.columns),
    };

    return webApiClient
      .fetchTableDataRowPosition(targetId, tableSpec.resultType, tableQuerySpec)
      .then((rowIndex: RowIndex) => rowIndex.rowIndex)
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ROW_POSITION_FAILURE,
          alertTitle: "Fetching of Row Position Failed",
          alertMessage: "Getting position of target id " + targetId + " in the data grid failed",
        }),
      );
  };
}

export function fetchGridColumn(columnId: number, tableQuerySpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ITableColumn> => {
    return webApiClient.fetchGridColumn(columnId, tableQuerySpec).catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_GRID_COLUMN_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

/**
 * Sets activeTable to null if it matches tableSpec.
 */
export function destroyActiveTable(tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState, _webApiClient: WebApiClient) => {
    // Clear the active table spec
    dispatch({
      type: DESTROY_ACTIVE_TABLE,
      payload: tableSpec,
    });
  };
}

export function clearTableDataSlice(tableSpec: InputTableQuerySpec | null | undefined): ReduxAction<void> {
  return (dispatch) => {
    // Reset the table data slice so that filter, sorting, selection model do not apply any longer to future tables to open
    // but only do it if the group is transient, since the standard saved groups can be paginated and need to keep them in memory
    if (!tableSpec?.idType || tableSpec.idType.toLowerCase() !== DATATABLE_ID_TYPE.GROUP.toLowerCase()) {
      dispatch({
        type: RESET_DATA_TABLE,
        payload: tableSpec,
      });
    }
  };
}

export function forwardToGroupRelated(data: MutationGroupByTypeArgs): ReduxAction<Promise<void>> {
  return (dispatch, _getState, webApiClient) => {
    dispatch({ type: GROUP_RELATED_REQUEST });

    // we open the new blank tab as long as we are in the context of the trusted event (user clicked)
    const newTab = new BrowserTab("/inprogress");
    // dispatch(auditGroupRelated(data));
    return webApiClient
      .groupRelated(data)
      .then((payload: Group) => {
        dispatch({
          type: GROUP_RELATED_SUCCESS,
          payload: payload,
        });

        // upon success we change the location of this new tab dynamically
        const destLocation: Location = { pathname: "/group/" + payload.id, search: "" };
        dispatch(auditRouteChange(null, destLocation));
        newTab.goTo(destLocation.pathname);
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: GROUP_RELATED_FAILURE,
          actionPayload: data,
          alertTitle: "Loading Related " + pluralize.plural(data.targetType) + " Failed",
        }),
      );
  };
}

export interface IClassicGroup {
  id: number;
  typeId: number;
}

export function createGroupInClassicPortal(input: MutationGroupInLegacyPortalArgs): ReduxAction<Promise<void>> {
  return (dispatch, getState, webApiClient) => {
    dispatch({ type: COPY_GROUP_TO_CLASSIC_PORTAL_REQUEST });

    // we open the new blank tab as long as we are in the context of the trusted event (user clicked)
    const newTab = new BrowserTab("/inprogress", `${input.tableQuerySpec.resultType}-portal-sel-classic`);

    return webApiClient
      .createGroupInClassicPortal(input)
      .then((payload: IClassicGroup) => {
        dispatch({
          type: COPY_GROUP_TO_CLASSIC_PORTAL_SUCCESS,
          payload: payload,
        });
        const appConfiguration: AppConfiguration = getState().config.configuration.application;
        // upon success we change the location of this new tab dynamically
        const fullPathname = urlRedirectToGroupPage(appConfiguration, payload.typeId, payload.id);
        const destLocation: Location = { pathname: fullPathname, search: "" } as Location;
        dispatch(auditRouteChange(null, destLocation));
        newTab.goToAbsoluteURL(fullPathname);
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: COPY_GROUP_TO_CLASSIC_PORTAL_FAILURE,
          actionPayload: {},
          alertTitle:
            "Sending " + pluralize.plural(input.tableQuerySpec.resultType) + " Group to Classic Portal Failed",
        }),
      );
  };
}

export function forwardToAnalyzer(
  table: ITableData | null,
  id: number | undefined,
  entityType: string,
  analyzer: string,
): ReduxAction<Promise<void>> {
  return (dispatch, getState, webApiClient) => {
    // Table is used for table displays, id for detail pages. Either one or the other is provided.
    const ids: number[] = table
      ? table.selectionModel.selectedIds.length > 0
        ? (table.selectionModel.selectedIds as number[])
        : table.rowIds
      : [id];
    const featureDisplayName: string = pluralize(FEATURE_DISPLAY_NAME[entityType]);

    const launchLimit = 100;

    if (ids.length > launchLimit) {
      notify.warning({
        title: selMessage({
          id: "table.forward.toAnalyzer.warningTitle",
          values: { featureName: capitalizeFirstLetter(featureDisplayName) },
        }),
        children: selMessage({
          id: "table.forward.toAnalyzer.warningMessage",
          values: {
            launchLimit: launchLimit,
          },
        }),
      });
      return;
    }

    // the following logic decides if we need to call the translation endpoint and what to retrieve
    const conversionTypes = getAnalyzerConversionEntityTypes(entityType, analyzer);

    if (conversionTypes) {
      const analyzerIdMappingInput: InputAnalyzerIdMappingInput = {
        inputEntityType: entityType,
        inputIds: ids,
        resultEntityTypes: conversionTypes,
      };

      return webApiClient
        .translateAnalyzerIds(analyzerIdMappingInput)
        .then((payload: AnalyzerIdMapping[]) => {
          // do not launch pathway analyzer from entities without pathway association
          if (
            analyzer === ANALYZERS.PATHWAY_ANALYZER &&
            payload.find((s: AnalyzerIdMapping) => s.entityType === ENTITY_TYPE.PATHWAY).idRanges.length === 0
          ) {
            notify.warning({
              title: selMessage({ id: "table.forward.toAnalyzer.translateAnalyzerIds.warningTitle" }),
              children: selMessage({
                id: "table.forward.toAnalyzer.translateAnalyzerIds.warningMessage",
                values: {
                  featureName: featureDisplayName,
                  analyzer: analyzer,
                },
              }),
            });
            return;
          }

          // do not launch locus analyzer from non-genomic transcripts and proteins
          if (analyzer === ANALYZERS.LOCUS_ANALYZER) {
            if (payload.find((s: AnalyzerIdMapping) => s.entityType === ENTITY_TYPE.GENE)?.idRanges.length === 0) {
              notify.warning({
                title: selMessage({ id: "table.forward.toAnalyzer.noGene.warningTitle" }),
                children: selMessage({
                  id: "table.forward.toAnalyzer.noGene.warningMessage",
                  values: {
                    featureName: featureDisplayName,
                    analyzer: analyzer,
                  },
                }),
              });
              return;
            }
            if (payload.find((s: AnalyzerIdMapping) => s.entityType === ENTITY_TYPE.CONTIG)?.idRanges.length === 0) {
              notify.warning({
                title: selMessage({ id: "table.forward.toAnalyzer.noSequence.warningTitle" }),
                children: selMessage({
                  id: "table.forward.toAnalyzer.noSequence.warningMessage",
                  values: {
                    featureName: featureDisplayName,
                    analyzer: analyzer,
                  },
                }),
              });
              return;
            }
          }

          if (
            analyzer === ANALYZERS.LOCUS_ANALYZER &&
            payload.find((s: AnalyzerIdMapping) => s.entityType === ENTITY_TYPE.CONTIG) &&
            payload.find((s: AnalyzerIdMapping) => s.entityType === ENTITY_TYPE.CONTIG).idRanges.length === 0
          ) {
            notify.warning({
              title: selMessage({ id: "table.forward.toAnalyzer.noOrganism.warningTitle" }),
              children: selMessage({
                id: "table.forward.toAnalyzer.noOrganism.warningMessage",
                values: {
                  featureName: featureDisplayName,
                  analyzer: analyzer,
                },
              }),
            });
            return;
          }

          callAnalyzer(payload, analyzer, dispatch, getState);
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: TRANSLATE_ANALYZER_IDS_FAILURE,
            actionPayload: {},
            alertTitle: "Translating Analyzer IDs Failed",
          }),
        );
    } else {
      // verify that no non-genomic gene is present when calling Locus Analyzer
      if (entityType === ENTITY_TYPE.GENE && analyzer === ANALYZERS.LOCUS_ANALYZER) {
        if (table) {
          // check when calling from any gene table by re-using its table spec
          const adHocTableSpec: InputTableQuerySpec = table.tableQuerySpec;
          adHocTableSpec.restrictions = {
            selection: {
              selectedResultIds: ids,
            },
          };

          return webApiClient
            .fetchTestGenomicGeneTable(adHocTableSpec)
            .then((payload: Table) => {
              if (payload.pageRows.find((r: Gene) => r.genome.id === NON_GENOMIC_GENOME_ID)) {
                notify.warning({
                  title: selMessage({ id: "table.forward.nonGenomicGene.warningTitle" }),
                  children: selMessage({ id: "table.forward.nonGenomicGene.warningMessage" }),
                });
                return;
              }
              callAnalyzerFromUntranslatedIds(ids, entityType, analyzer, dispatch, getState);
            })
            .catch(
              createErrorHandler(dispatch, {
                actionType: null,
                actionPayload: {},
                alertTitle: "Getting Gene Rows for Genomic Testing Failed",
              }),
            );
        } else {
          // check when calling from a gene summary page (no table spec, but gene available in redux store)
          const gene = getState().gene.gene;
          if (gene.genome.id === NON_GENOMIC_GENOME_ID) {
            notify.warning({
              title: selMessage({ id: "table.forward.nonGenomicGene.warningTitle" }),
              children: selMessage({ id: "table.forward.singleGeneNoGenomeAssociation.warningMessage" }),
            });
            return;
          }
        }
      } else if (entityType === ENTITY_TYPE.PROJECT_FILE) {
        // this is fake: We abuse the ID translation endpoint to find out if the msa or fasta file is a protein or nucleotide file
        const analyzerIdMappingInput: InputAnalyzerIdMappingInput = {
          inputEntityType: entityType,
          inputIds: ids,
          resultEntityTypes: [entityType],
        };

        return webApiClient
          .translateAnalyzerIds(analyzerIdMappingInput)
          .then((payload: AnalyzerIdMapping[]) => {
            const dynamicAnalyzer: string =
              payload[0]?.entityType === ENTITY_TYPE.PROTEIN ? ANALYZERS.PROTEIN_ANALYZER : ANALYZERS.DNA_ANALYZER;
            callAnalyzerFromUntranslatedIds(ids, entityType, dynamicAnalyzer, dispatch, getState);
          })
          .catch(
            createErrorHandler(dispatch, {
              actionType: null,
              actionPayload: {},
              alertTitle: "Getting Sequence Type for FASTA/MSA File Failed",
            }),
          );
      }

      callAnalyzerFromUntranslatedIds(ids, entityType, analyzer, dispatch, getState);
    }
  };
}

function callAnalyzerFromUntranslatedIds(ids: number[], entityType: string, analyzer: string, dispatch, getState) {
  const idRanges: IdRangePair[] = ids.map((idValue: number) => {
    return { id: idValue, range: null };
  });
  const analyzerInputIds: AnalyzerIdMapping[] = [{ entityType: entityType, idRanges }];
  callAnalyzer(analyzerInputIds, analyzer, dispatch, getState);
}

function callAnalyzer(inputIds: AnalyzerIdMapping[], analyzer: string, dispatch: any, getState: any) {
  if (inputIds) {
    const appConfiguration: AppConfiguration = getState().config.configuration.application;
    // upon success we change the location of this new tab dynamically
    const fullPathname = urlJnlpPage(appConfiguration, inputIds, analyzer);
    const destLocation: Location = { pathname: fullPathname, search: "" } as Location;
    dispatch(auditRouteChange(null, destLocation));

    // some testing indicated that this simple JS call is the best cross-browser approach,
    // see also https://stackoverflow.com/questions/40707684
    window.open(fullPathname);
  }
}

export function fetchColumnTemplates(tableSpec: ITableSpecWithContext) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Template[]> => {
    dispatch({
      type: FETCH_COLUMN_TEMPLATES_REQUEST,
      payload: tableSpec,
    });

    const queryParams: IDataTableQueryParams = {
      tableQuerySpec: tableSpec,
      withColumns: true,
    };

    return (
      webApiClient
        .fetchTableData(queryParams)
        //return new Promise((resolve) => setTimeout(() => resolve(getState().tableData.tables[getTableDataKey(tableSpec)].columnTemplates), 1000))
        .then((table: any) => {
          dispatch({
            type: FETCH_COLUMN_TEMPLATES_SUCCESS,
            payload: { templates: table.templates, tableSpec },
          });
          return table.templates;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: FETCH_COLUMN_TEMPLATES_FAILURE,
            actionPayload: tableSpec,
            alertTitle: "Fetching of Column Templates Failed",
          }),
        )
    );
  };
}

export function setColumnTemplateAsSelected(columnTemplate: Template, tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch({
      type: SELECT_COLUMN_TEMPLATE_REQUEST,
      payload: tableSpec,
    });

    return webApiClient
      .loadColumnTemplate(columnTemplate, tableSpec)
      .then((template: Template) => {
        dispatch({
          type: SELECT_COLUMN_TEMPLATE_SUCCESS,
          payload: { template, tableSpec }, // not used currently
        });
        //dispatch(fetchColumnTemplates(tableSpec));
        return true;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SELECT_COLUMN_TEMPLATE_FAILURE,
          actionPayload: tableSpec,
          alertTitle: "Column Template Selection Failed",
        }),
      );
  };
}

export function deleteTemplate(columnTemplate: Template, tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    dispatch({
      type: DELETE_COLUMN_TEMPLATE_REQUEST,
      payload: tableSpec,
    });

    return webApiClient
      .deleteColumnTemplate(columnTemplate.id)
      .then((done: boolean) => {
        if (done) {
          dispatch({
            type: DELETE_COLUMN_TEMPLATE_SUCCESS,
            payload: tableSpec,
          });
        }
        //dispatch(fetchColumnTemplates(tableSpec));
        return done;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_COLUMN_TEMPLATE_FAILURE,
          actionPayload: tableSpec,
          alertTitle: "Column Template Deletion Failed",
        }),
      );
  };
}

export function saveNewTemplate(name: string, tableSpec: InputTableQuerySpec, columns: ITableColumn[]) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Template> => {
    const inputColumns: InputColumnInput[] = columns.map((c: ITableColumn) => ({
      columnId: c.columnId,
      hidden: c.hidden,
      namespace: c.namespace,
    }));

    dispatch({
      type: SAVE_COLUMN_TEMPLATE_REQUEST,
      payload: tableSpec,
    });

    return webApiClient
      .saveColumnTemplate(name, tableSpec, inputColumns)
      .then((columnTemplate: Template) => {
        dispatch({
          type: SAVE_COLUMN_TEMPLATE_SUCCESS,
          payload: tableSpec,
        });
        //dispatch(fetchColumnTemplates(tableSpec));
        return columnTemplate;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SAVE_COLUMN_TEMPLATE_FAILURE,
          actionPayload: tableSpec,
          alertTitle: "Column Template Creation Failed",
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function updateExistingTemplate(
  columnTemplate: Template,
  orderedColumns: ITableColumn[],
  tableSpec: InputTableQuerySpec,
) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Template> => {
    dispatch({
      type: UPDATE_COLUMN_TEMPLATE_REQUEST,
      payload: tableSpec,
    });

    const columns: InputColumnInput[] = orderedColumns.map((c: ITableColumn) => {
      const { columnId, namespace, hidden } = c;
      return {
        columnId: columnId,
        namespace: namespace,
        hidden: hidden,
      };
    });

    return (
      webApiClient
        .updateColumnTemplate(columnTemplate.id, columns, tableSpec)
        //return new Promise((resolve) => setTimeout(resolve, 1000))
        .then((ct: Template) => {
          dispatch({
            type: UPDATE_COLUMN_TEMPLATE_SUCCESS,
            payload: tableSpec,
          });
          // dispatch(fetchColumnTemplates(tableSpec));
          return ct;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: UPDATE_COLUMN_TEMPLATE_FAILURE,
            actionPayload: tableSpec,
            alertTitle: "Column Template Update Failed",
            showAlert: false,
            throwError: true,
          }),
        )
    );
  };
}

export function updateTableGuidanceState(doNotShowPerfGuidance: boolean) {
  return {
    type: UPDATE_TABLE_GUIDANCE_STATE,
    payload: doNotShowPerfGuidance,
  };
}

export function setExpansionPath(tableQuerySpec: InputTableQuerySpec, expansionPath: string[]) {
  return {
    type: SET_EXPANSION_PATH,
    payload: {
      tableQuerySpec: tableQuerySpec,
      expansionPath: expansionPath,
    },
  };
}

export function ensureRowIsVisible(
  tableQuerySpec: InputTableQuerySpec,
  rowId: TGridSelModelIdentifier,
  rowIndex?: number,
) {
  return {
    type: ENSURE_ROW_VISIBLE,
    payload: {
      tableQuerySpec: tableQuerySpec,
      rowId: rowId,
      rowIndex: rowIndex,
    },
  };
}

export function onRestrictionChange(tableRestriction: TableRestriction) {
  return {
    type: CHANGE_TABLE_RESTRICTION,
    payload: tableRestriction,
  };
}

export function updateTableRow(tableQuerySpec: InputTableQuerySpec, rowData: IIdentifiable) {
  return {
    type: UPDATE_TABLE_ROW,
    payload: {
      tableQuerySpec,
      rowData,
    },
  };
}

/**** utils ****/
// getting active expansion path takes into account the hidden columns
export function getActiveExpansionPath(tableData: ITableData): string[] {
  return tableData.expansionPath?.filter((expansionKey) => getExpandedColumnsByKey(tableData, expansionKey));
}

export function getExpandedColumnsByKey(tableData: ITableData, expansionKey: string): ITableColumn[] {
  return tableData.columns
    .filter((column: ITableColumn) => !column.hidden)
    .filter((column: ITableColumn) => getCellExpansionKey(column) === expansionKey);
}

export function isColumnExpanded(column: Partial<ITableColumn | ICustomColDef>, expansionPath: string[]): boolean {
  return !!expansionPath?.find((expansionKey) => getCellExpansionKey(column) === expansionKey);
}

// all column having the same expansion key are expanded together
export function getCellExpansionKey(column: Partial<ITableColumn | ICustomColDef>): string {
  switch (column.cellExpansion) {
    case CELL_EXPANSION.STANDALONE:
      return column.field;
    case CELL_EXPANSION.GROUP:
      return column.groupField;
    case CELL_EXPANSION.CONTEXT:
      return column.multiValuesContext;
  }
}

export function showGuidanceMessageForEntity(tableSpec: InputTableQuerySpec) {
  // do not show the "Please consider adjusting..." message for VCF variant tables (RZ is sensitive wrt performance topics)
  return tableSpec?.resultType !== ENTITY_TYPE.VCF_VARIANT;
}

/*
export function isColumnGroupExpanded(columns: Array<ICustomColDef>, expansionPath: string): boolean {
  return !!columns.find((column: ICustomColDef) => column.cellExpansion === CELL_EXPANSION.GROUP && column.groupField === expansionPath);
}
*/
